import type { ReCaptchaInstance } from 'recaptcha-v3'

/**
 * Global configuration interface for reCAPTCHA loading state.
 *
 * @interface IGlobalConfig
 * @property {Array<{resolve: Function, reject: Function}>} loadedWaiters - Waiting promises for reCAPTCHA loading.
 * @property {Error | null} error - Potential loading error.
 */
interface IGlobalConfig {
  loadedWaiters: Array<{
    resolve: (resolve: boolean) => void
    reject: (reject: Error) => void
  }>
  error: Error | null
}

/**
 * Global configuration object for reCAPTCHA loading.
 *
 * @type {IGlobalConfig}
 */
const globalConfig: IGlobalConfig = {
  loadedWaiters: [],
  error: null,
}

/**
 * Provides a reactive state for reCAPTCHA loading status.
 *
 * @returns {Ref<boolean>} Reactive reference to reCAPTCHA loading state.
 */
const useRecaptchaLoaded = () => useState('is-recaptcha-loaded', () => false)

/**
 * Provides a reactive state for reCAPTCHA instance.
 *
 * @returns {Ref<ReCaptchaInstance | null>} Reactive reference to reCAPTCHA instance.
 */
const useRecaptchaInstance = () =>
  useState<ReCaptchaInstance | null>('recaptcha-instance', () => null)

/**
 * Loads reCAPTCHA library and initializes the instance.
 *
 * @async
 * @returns {Promise<ReCaptchaInstance | null>} Initialized reCAPTCHA instance.
 *
 * @remarks
 * Ensures reCAPTCHA is loaded only once and caches the instance.
 */
const useRecaptchaLoader = async () => {
  const config = useRuntimeConfig()
  const isLoaded = useRecaptchaLoaded()
  const instance = useRecaptchaInstance()
  if (isLoaded.value) return instance
  const { load } = await import('recaptcha-v3')
  const _instance = await load(config.public.recaptcha.key, {
    autoHideBadge: true,
    useEnterprise: true,
  })
  isLoaded.value = true
  instance.value = _instance
  return instance
}

/**
 * Provides reCAPTCHA utility functions.
 *
 * @async
 * @returns {Object} reCAPTCHA utilities.
 * @property {ReCaptchaInstance} instance - reCAPTCHA instance.
 * @property {boolean} isLoaded - Whether reCAPTCHA is loaded.
 * @property {Function} executeRecaptcha - Function to execute reCAPTCHA.
 * @property {Function} recaptchaLoaded - Function to check reCAPTCHA loading.
 *
 * @example
 * const { instance, executeRecaptcha } = await useReCaptcha()
 */
export const useReCaptcha = async () => {
  const instance = await useRecaptchaLoader()
  const isLoaded = useRecaptchaLoaded()

  return {
    instance,
    isLoaded: isLoaded.value,
    executeRecaptcha: recaptcha(instance),
    recaptchaLoaded: recaptchaLoaded(isLoaded),
  }
}

/**
 * Composable for site-wide reCAPTCHA functionality.
 *
 * @returns {Object} reCAPTCHA site methods.
 * @property {Function} getRecaptchaToken - Generates reCAPTCHA token.
 * @property {Function} verifyReCaptchaToken - Verifies reCAPTCHA token.
 * @property {Function} checkTokenAndVerify - Checks and verifies reCAPTCHA token.
 * @property {Function} checkTokenReCAPTCHA - Comprehensive reCAPTCHA validation.
 *
 * @example
 * const { checkTokenReCAPTCHA } = useRecaptchaSite()
 * await checkTokenReCAPTCHA('login')
 */
export const useRecaptchaSite = () => {
  // @ts-ignore
  const { addSnackbar } = useHeaderSnackbars()

  /**
   * Generates a reCAPTCHA token for a specific action.
   *
   * @param {string} action - The action to generate token for.
   * @returns {Promise<string>} Generated reCAPTCHA token.
   */
  const getRecaptchaToken = async (action: string) => {
    const { executeRecaptcha } = await useReCaptcha()
    const token = await executeRecaptcha(action)
    return token
  }

  /**
   * Verifies a reCAPTCHA token via server-side validation.
   *
   * @param {string} token - reCAPTCHA token to verify.
   * @returns {Promise<Object>} Verification result.
   */
  const verifyReCaptchaToken = async (token: string) => {
    const response = await $fetch(`/api/recaptcha/validateRecaptcha/${token}`, {
      method: 'GET',
    })
    const value = ref(response)
    return {
      data: value,
    }
  }
  type ResultsReCaptcha = {
    resultToken: boolean
    resultVerifyReCaptchaToken: boolean
  }

  /**
   * Comprehensive reCAPTCHA token generation and verification.
   *
   * @param {string} action - The action to validate.
   * @returns {Promise<ResultsReCaptcha>} Token generation and verification results.
   */
  const checkTokenAndVerify = async (
    action: string
  ): Promise<ResultsReCaptcha> => {
    const token = await getRecaptchaToken(action)
    const verifyToken = token ? await verifyReCaptchaToken(token) : null
    if (!token) {
      return {
        resultToken: false,
        resultVerifyReCaptchaToken: false,
      }
    } else if (
      verifyToken &&
      (verifyToken.data.value as any).response.success
    ) {
      return {
        resultToken: true,
        resultVerifyReCaptchaToken: true,
      }
    } else {
      return {
        resultToken: false,
        resultVerifyReCaptchaToken: false,
      }
    }
  }

  /**
   * Validates reCAPTCHA and handles error scenarios.
   *
   * @param {string} message - Context message for error handling.
   * @returns {Promise<boolean>} Validation result.
   * @throws {Error} If reCAPTCHA validation fails.
   */
  const checkTokenReCAPTCHA = async (message: string) => {
    const check = await checkTokenAndVerify(message)
    if (!check.resultToken || !check.resultVerifyReCaptchaToken) {
      addSnackbar(message, {
        message: {
          key: 'generic.recaptchaError',
        },
        duration: 5,
        textClasses: 'text-primitives-red',
      })
      throw createError({
        statusCode: 422,
        statusMessage: `[ERROR]: reCAPTHCA failed on form: ${message}`,
      })
    }
    return true
  }

  return {
    getRecaptchaToken,
    verifyReCaptchaToken,
    checkTokenAndVerify,
    checkTokenReCAPTCHA,
  }
}

/**
 * Creates a function to check reCAPTCHA loading status.
 *
 * @param {Ref<boolean>} isLoaded - Reactive reference to loading state.
 * @returns {Function} Promise-based loading check function.
 *
 * @private
 */
function recaptchaLoaded(isLoaded: Ref<boolean>) {
  // eslint-disable-next-line @typescript-eslint/promise-function-async
  return () =>
    new Promise<boolean>((resolve, reject) => {
      if (globalConfig.error !== null) {
        return reject(globalConfig.error)
      }
      if (isLoaded.value) {
        return resolve(true)
      }
      globalConfig.loadedWaiters.push({ resolve, reject })
    })
}

/**
 * Creates a function to execute reCAPTCHA for a specific action.
 *
 * @param {Ref<ReCaptchaInstance | null>} instance - reCAPTCHA instance reference.
 * @returns {Function} Function to execute reCAPTCHA.
 *
 * @private
 */
function recaptcha(instance: Ref<ReCaptchaInstance | null>) {
  return async (action: string): Promise<string | undefined> => {
    return await instance.value?.execute(action)
  }
}
